import { sanitizeText } from "./writing-assistant-sanitizer";
import { apiConnection } from "@/services/api-connection";
import { writingAssistantConstantes } from "@/constantes/writingAssistant";

const QUESTIONS_URL = "/app/front/writing-assistant/questions";
const QUOTA_URL = "/app/front/writing-assistant/quota";
const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const MS_PER_SECOND = 1000;
const CODE_QUOTA_REACHED = "quota_reached";
const CODE_IN_PROGRESS = "in_progress";
const OUTCOME_TIMEOUT = Symbol("timeout");
const OUTCOME_ABORTED = Symbol("aborted");

class WritingAssistantManager {
  constructor() {
    this.controller = null;
  }

  async askQuestions(data) {
    this.stop();

    const controller = new AbortController();
    this.controller = controller;

    const sanitizedData = { ...data };
    if (sanitizedData.text !== undefined) sanitizedData.text = sanitizeText(sanitizedData.text);
    if (sanitizedData.activity_title !== undefined)
      sanitizedData.activity_title = sanitizeText(sanitizedData.activity_title);

    let timer;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve(OUTCOME_TIMEOUT), writingAssistantConstantes.REQUEST_TIMEOUT);
    });
    const aborted = new Promise((resolve) => {
      controller.signal.addEventListener("abort", () => resolve(OUTCOME_ABORTED), { once: true });
    });

    let response;
    try {
      response = await Promise.race([
        apiConnection.post(QUESTIONS_URL, sanitizedData, true, true, null, true),
        timeout,
        aborted,
      ]);
    } catch (err) {
      if (err?.name === "AbortError" || controller.signal.aborted) return { status: "aborted" };
      return { status: "error", ...this.classifyError(err) };
    } finally {
      clearTimeout(timer);
    }

    if (response === OUTCOME_ABORTED) return { status: "aborted" };
    if (response === OUTCOME_TIMEOUT) return { status: "error", kind: "generic", message: null };

    if (controller.signal.aborted) return { status: "aborted" };

    const items = Array.isArray(response?.questions) ? response.questions : [];
    return {
      status: "success",
      questions: items.map((item) => ({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        text: item.question,
      })),
      quota: response?.quota ?? null,
      resetAt: response?.quota?.resetAt ?? null,
    };
  }

  async fetchQuota(params) {
    let timer;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve(OUTCOME_TIMEOUT), writingAssistantConstantes.QUOTA_TIMEOUT);
    });
    try {
      const response = await Promise.race([apiConnection.get(QUOTA_URL, params, true, false, true), timeout]);
      if (response === OUTCOME_TIMEOUT) return null;
      if (typeof response?.attemptsRemaining !== "number") return null;
      return {
        attemptsRemaining: response.attemptsRemaining,
        nextMinLength: typeof response.nextMinLength === "number" ? response.nextMinLength : null,
        resetAt: response.resetAt ?? null,
      };
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  classifyError(err) {
    if (err?.status === HTTP_FORBIDDEN) {
      return { kind: "forbidden", message: err.error ?? err.message ?? null };
    }
    if (err?.code === CODE_QUOTA_REACHED) {
      return {
        kind: "quota",
        code: CODE_QUOTA_REACHED,
        message: err.error ?? err.message ?? null,
        resetAt: err.resetAt ?? this.resetAtFromRetryAfter(err.retryAfter),
      };
    }
    if (err?.code === CODE_IN_PROGRESS) {
      return { kind: "busy", code: CODE_IN_PROGRESS, message: err.error ?? err.message ?? null };
    }
    if (typeof err?.minLength === "number") {
      return {
        kind: "tooShort",
        message: err.error ?? null,
        minLength: err.minLength,
        attemptsRemaining: typeof err.attemptsRemaining === "number" ? err.attemptsRemaining : null,
        resetAt: err.resetAt ?? null,
      };
    }
    const isClientError = err?.status >= HTTP_BAD_REQUEST && err.status < HTTP_INTERNAL_SERVER_ERROR;
    return { kind: "generic", message: isClientError ? (err.error ?? null) : null };
  }

  resetAtFromRetryAfter(retryAfter) {
    if (typeof retryAfter !== "number" || retryAfter <= 0) return null;
    return new Date(Date.now() + retryAfter * MS_PER_SECOND).toISOString();
  }

  generateDraftId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  }

  stop() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }
}

export const writingAssistantManager = new WritingAssistantManager();
